import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField } from '@mui/material';
import { Box } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';

import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import HeartBrokenIcon from '@mui/icons-material/HeartBroken';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LayersIcon from '@mui/icons-material/Layers';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { authorColor, mergePublicAndPrivateItems } from '../../common/ItemTools';
import { createAuthor } from '../../redux/features/authors/authorsSlice';
import { createTag, selectTagVocabulary } from '../../redux/features/tags/tagsSlice';
import renderTagOption, { tagPopperSlotProps } from '../common/TagOption';
import { batchUpdateItems } from '../../redux/features/items/itemsSlice';
import { setSnackbar } from '../../redux/features/snackbarSlice';
import {
  EDIT_FONT_SIZE, MUTED, SANS_STACK, SERIF_STACK, TEXT_DARK, TEXT_SECONDARY,
} from '../common/tokens';
import {
  BackButton, ChipRemove, GroupLabel, LinkButton, OutlinedButton, PrimaryButton,
  TagChip, TextInput, TextArea, autocompleteSx,
} from '../common/hero/styles';
import {
  withCreateOption, optionLabel, normalizeTag, followableUrl,
  inputToTimestamp, inputDateTimeToUnix,
} from '../common/hero/utils';
import { VISIBILITY_HIDDEN, VISIBILITY_VISIBLE } from '../../common/Visibility';
import { parseReadTime } from './utils';
import ItemHeroEditForm from './EditForm';

// Coral tints specific to the batch banner; the brand coral itself comes from
// the theme (see tokens.js).
const BANNER_BG = '#fdf4f1';
const BANNER_BORDER = '#f6d3c8';
const BANNER_CHIP_BORDER = '#f0cabd';

// 'keep' / '__keep' mean "leave this field alone on every selected item".
const KEEP = 'keep';
const LANG_KEEP = '__keep';

// Descriptor shapes shared by most fields. A text field is set once it holds
// non-whitespace; a choice field is set once it moves off its "keep" value;
// a list field is set once it holds an entry.
const textField = (key, toPatch) => ({
  key, empty: '', isSet: (v) => Boolean(v.trim()), toPatch,
});
const choiceField = (key, empty, toPatch) => ({
  key, empty, isSet: (v) => v !== empty, toPatch,
});
const listField = (key, isSet, toPatch) => ({
  key, empty: [], isSet, toPatch,
});
const dateField = (key, patchKey) => textField(key, (v) => {
  const ts = inputDateTimeToUnix(v);
  return ts ? { [patchKey]: ts } : {};
});

/**
 * Every editable field, declared once. `empty` is its blank draft value,
 * `isSet` decides whether the user filled it in (blank fields keep each item's
 * own value), and `toPatch` maps it into the batch patch — `toPatchAsync` for
 * the two that must create entities before they have ids to send.
 *
 * Array fields replace rather than merge: the backend batch patch has no
 * per-item merge.
 */
const FIELDS = [
  textField('title', (v) => ({ title: v.trim() })),
  textField('excerpt', (v) => ({ excerpt: v.trim() })),
  textField('coverImageUrl', (v) => ({ coverImageUrl: v.trim() })),
  textField('readTime', (v) => ({ timeNeeded: parseReadTime(v) })),
  choiceField('type', '', (v) => ({ type: Number(v) })),
  choiceField('lang', LANG_KEEP, (v) => ({ lang: v })),
  choiceField('fav', KEEP, (v) => ({ isFavorite: v === 'on' })),
  choiceField('hidden', KEEP, (v) => ({ visibility: v === 'on' ? VISIBILITY_HIDDEN : VISIBILITY_VISIBLE })),
  dateField('dateCreated', 'timeCreated'),
  dateField('dateReviewed', 'timeReviewed'),
  // Reads the dateOnly checkbox too, and patches two keys, so it isn't a dateField.
  textField('datePublished', (v, draft) => {
    const ts = inputToTimestamp(v, draft.dateOnly);
    return ts ? { timePublished: ts, timePublishedDateOnly: Boolean(draft.dateOnly) } : {};
  }),
  listField(
    'urls',
    (v) => v.some((u) => followableUrl(u.url)),
    (v) => ({
      links: v
        .map((u) => followableUrl(u.url))
        .filter(Boolean)
        .map((url, i) => (i === 0 ? { url, primary: true } : { url, visible: true })),
    }),
  ),
  listField(
    'related',
    (v) => v.length > 0,
    (v) => ({ relatedItems: v.map((r) => ({ id: r.id })) }),
  ),
  {
    key: 'tags',
    empty: [],
    isSet: (v) => v.length > 0,
    toPatchAsync: async (v, draft, dispatch) => ({
      tagIds: await Promise.all(v.map(async (t) => (
        t.id || (await dispatch(createTag({ tag: { name: t.name } })).unwrap()).id
      ))),
    }),
  },
  {
    key: 'authors',
    empty: [],
    isSet: (v) => v.length > 0,
    toPatchAsync: async (v, draft, dispatch) => ({
      authorIds: await Promise.all(v
        .filter((a) => a.authorId || a.name.trim())
        .map(async (a) => (
          a.authorId || (await dispatch(createAuthor({
            author: { profiles: [{ name: a.name.trim() }] },
          })).unwrap()).id
        ))),
    }),
  },
];

// `dateOnly` modifies how `datePublished` is read rather than patching anything
// of its own, so it lives outside FIELDS.
const emptyDraft = () => ({
  ...Object.fromEntries(FIELDS.map((f) => [f.key, Array.isArray(f.empty) ? [] : f.empty])),
  dateOnly: false,
});

const setFields = (draft) => FIELDS.filter((f) => f.isSet(draft[f.key]));

const buildPatch = async (draft, dispatch) => {
  const fragments = await Promise.all(setFields(draft).map((f) => (
    f.toPatchAsync
      ? f.toPatchAsync(draft[f.key], draft, dispatch)
      : f.toPatch(draft[f.key], draft)
  )));
  return Object.assign({}, ...fragments);
};

// Icon, color and caption for each state of the two tri-state toggles. The
// active ("on") state is coral, so these are built from the theme's primary.
const favStates = (coral) => ({
  keep: { icon: FavoriteBorderIcon, color: '#3c3c3c', cap: 'keep', capColor: MUTED },
  on: { icon: FavoriteIcon, color: coral, cap: 'favorite all', capColor: coral },
  off: { icon: HeartBrokenIcon, color: MUTED, cap: 'unfavorite all', capColor: TEXT_SECONDARY },
});
const hiddenStates = (coral) => ({
  keep: { icon: VisibilityOffIcon, color: '#3c3c3c', cap: 'keep', capColor: MUTED },
  on: { icon: VisibilityOffIcon, color: coral, cap: 'hide all', capColor: coral },
  off: { icon: VisibilityIcon, color: '#3c3c3c', cap: 'show all', capColor: TEXT_SECONDARY },
});

const pageSx = {
  position: 'fixed',
  inset: 0,
  zIndex: 1500,
  background: '#fff',
  overflowY: 'auto',
  fontFamily: SANS_STACK,
};
const flagToggleSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '3px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
};
const flagCaptionSx = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '.04em',
  textTransform: 'uppercase',
};
const itemChipSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
  background: '#fff',
  border: `1px solid ${BANNER_CHIP_BORDER}`,
  borderRadius: '999px',
  padding: '3px 10px 3px 8px',
  fontSize: '0.8rem',
  color: '#3C3C3C',
  maxWidth: 260,
};

// A keep / on / off toggle, cycled by clicking. `states` supplies the icon,
// colors and caption for each of the three states.
function FlagToggle({ states, value, title, onClick }) {
  const state = states[value];
  const Icon = state.icon;
  return (
    <Box component="button" type="button" onClick={onClick} title={title} sx={flagToggleSx}>
      <Icon sx={{ fontSize: 22, color: state.color }} />
      <Box component="span" sx={{ ...flagCaptionSx, color: state.capColor }}>
        {state.cap}
      </Box>
    </Box>
  );
}

FlagToggle.propTypes = {
  states: PropTypes.object.isRequired,
  value: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

// Full-screen batch editor. Blank fields keep each item's own value; filled
// fields overwrite every selected item.
export default function BatchEditHeroPage(props) {
  const { items, namespace, onClose, onSaved } = props;

  const dispatch = useDispatch();
  const coral = useTheme().palette.primary.main;
  const userTags = useSelector(selectTagVocabulary);

  const favMap = useMemo(() => favStates(coral), [coral]);
  const hiddenMap = useMemo(() => hiddenStates(coral), [coral]);

  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [newRelated, setNewRelated] = useState('');

  const displayItems = useMemo(
    () => items.map((it) => mergePublicAndPrivateItems(it)),
    [items],
  );
  const count = displayItems.length;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape'
        && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const cycleFlag = (key) => setDraft((d) => {
    const next = { keep: 'on', on: 'off', off: KEEP }[d[key]];
    return { ...d, [key]: next };
  });
  const toggleDateOnly = () => setDraft((d) => {
    const dateOnly = !d.dateOnly;
    const date = (d.datePublished || '').slice(0, 10);
    const datePublished = date && !dateOnly ? `${date}T00:00` : date;
    return { ...d, dateOnly, datePublished };
  });

  const addAuthorEntry = (entry) => setDraft((d) => {
    if (d.authors.some((a) => (a.authorId && a.authorId === entry.authorId)
      || a.name.toLowerCase() === entry.name.toLowerCase())) return d;
    return { ...d, authors: [...d.authors, { ...entry, color: authorColor(entry.name) }] };
  });
  const removeAuthor = (idx) => setField('authors', draft.authors.filter((_, i) => i !== idx));

  const addUrl = () => setField('urls', [...draft.urls, { url: '' }]);
  const setUrl = (idx, value) => setField('urls', draft.urls.map((u, i) => (i === idx ? { url: value } : u)));
  const removeUrl = (idx) => setField('urls', draft.urls.filter((_, i) => i !== idx));

  const addTag = (value) => {
    if (!value) return;
    let entry = null;
    if (typeof value === 'string' || value.inputValue) {
      const name = normalizeTag(typeof value === 'string' ? value : value.inputValue);
      if (name) entry = { name };
    } else {
      entry = { id: value.id, name: value.name };
    }
    if (!entry) return;
    setDraft((d) => (d.tags.some((t) => t.name === entry.name)
      ? d : { ...d, tags: [...d.tags, entry] }));
    setNewTag('');
  };
  const removeTag = (name) => setField('tags', draft.tags.filter((t) => t.name !== name));

  const addRelated = () => {
    const id = newRelated.trim().replace(/^#/, '');
    if (!id) return;
    setDraft((d) => (d.related.some((r) => r.id === id)
      ? d : { ...d, related: [...d.related, { id }] }));
    setNewRelated('');
  };
  const removeRelated = (id) => setField('related', draft.related.filter((r) => r.id !== id));

  const canSave = setFields(draft).length > 0 && count > 0 && !saving;
  const tagOptions = userTags.filter((t) => !draft.tags.some((dt) => dt.id === t.id));

  const onSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const patch = await buildPatch(draft, dispatch);
      const ids = displayItems.map((it) => it.id);
      if (Object.keys(patch).length) {
        await dispatch(batchUpdateItems({ namespace, ids, patch })).unwrap();
        window.dispatchEvent(new CustomEvent('itemsChanged'));
      }
      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      dispatch(setSnackbar({
        snackbarOpen: true,
        snackbarType: 'error',
        snackbarMessage: 'Failed to update items.',
      }));
    } finally {
      setSaving(false);
    }
  };

  // Batch mode never previews an individual item's cover — only a cover the
  // user explicitly enters (which would overwrite every selected item).
  const coverSrc = draft.coverImageUrl.trim();

  return (
    <Box data-screen-label="Item hero page (batch edit)" sx={pageSx}>
      {/* Toolbar */}
      <Box sx={{ maxWidth: 1232, mx: 'auto', padding: '16px 28px 0', display: 'flex', alignItems: 'center' }}>
        <BackButton type="button" onClick={onClose}>
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
          Back
        </BackButton>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <OutlinedButton type="button" onClick={onClose}>Cancel</OutlinedButton>
          <PrimaryButton type="button" onClick={onSave} disabled={!canSave}>
            <CheckIcon sx={{ fontSize: 16 }} />
            {`Save to ${count}`}
          </PrimaryButton>
        </Box>
      </Box>

      {/* Batch banner */}
      <Box sx={{ maxWidth: 1232, margin: '16px auto 0', padding: '0 28px' }}>
        <Box sx={{
          background: BANNER_BG, border: `1px solid ${BANNER_BORDER}`, borderRadius: '8px', padding: '12px 16px',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', mb: '10px' }}>
            <LayersIcon sx={{ fontSize: 19, color: coral }} />
            <Box component="span" sx={{ fontSize: '0.9rem', color: '#3C3C3C' }}>
              {'Batch editing '}
              <strong style={{ color: coral }}>{`${count} ${count === 1 ? 'item' : 'items'}`}</strong>
              {'. Fields you fill in overwrite all selected items; blank fields keep each item’s own value. '}
              <strong>Tags, authors, source URLs and related items replace</strong>
              {' the existing values on every selected item.'}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {displayItems.map((it) => (
              <Box key={it.id} sx={itemChipSx}>
                <Box component="span" sx={{
                  width: 16, height: 16, borderRadius: '3px', flexShrink: 0, background: '#f0f0f0',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 700, color: TEXT_SECONDARY,
                }}>
                  {(it.title || '?').trim()[0]?.toUpperCase() || '?'}
                </Box>
                <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {it.title || '(untitled)'}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Cover */}
      <Box sx={{ maxWidth: 1232, margin: '18px auto 0', padding: '0 28px' }}>
        <Box sx={{
          position: 'relative', width: '100%', height: 300, borderRadius: '2px', overflow: 'hidden',
          background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {coverSrc ? (
            <Box component="img" src={coverSrc} alt="" sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
          ) : (
            <Box sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
              color: MUTED, fontSize: 13,
            }}>
              <ImageOutlinedIcon sx={{ fontSize: 40 }} />
              Covers unchanged — paste a URL to set one for all
            </Box>
          )}
          {coverSrc && count > 1 && (
            <Box sx={{
              position: 'absolute', bottom: 10, left: 10, background: 'rgba(60,60,60,0.9)', color: '#fff',
              borderRadius: '4px', padding: '4px 10px', fontSize: 12, fontWeight: 700,
            }}>
              {`Cover applies to all ${count} items`}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '8px' }}>
          <Box component="span" sx={{ fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, whiteSpace: 'nowrap' }}>
            Cover URL
          </Box>
          <TextInput
            value={draft.coverImageUrl}
            onChange={(e) => setField('coverImageUrl', e.target.value)}
            placeholder="Paste an image URL to set one cover for all selected"
            style={{ flex: 1 }}
          />
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ maxWidth: 1064, mx: 'auto', padding: '24px 28px 60px' }}>

        {/* Type / Language / Read time / Authors / Publishing / Source URLs —
            shared with the single-item hero page in keep/overwrite mode. */}
        <ItemHeroEditForm
          batch
          draft={draft}
          setField={setField}
          toggleDateOnly={toggleDateOnly}
          addAuthorEntry={addAuthorEntry}
          removeAuthor={removeAuthor}
          addUrl={addUrl}
          setUrl={setUrl}
          removeUrl={removeUrl}
        />

        {/* Title */}
        <Box sx={{ mt: '22px' }}>
          <GroupLabel>Title</GroupLabel>
          <TextArea
            value={draft.title}
            onChange={(e) => setField('title', e.target.value)}
            rows={2}
            placeholder="Leave blank to keep each item's title"
            style={{
              width: '100%', fontFamily: SERIF_STACK, fontWeight: 700,
              fontSize: EDIT_FONT_SIZE, lineHeight: 1.25, color: TEXT_DARK, padding: '10px 14px',
            }}
          />
        </Box>

        {/* Excerpt */}
        <Box sx={{ mt: '20px' }}>
          <GroupLabel>Excerpt</GroupLabel>
          <TextArea
            value={draft.excerpt}
            onChange={(e) => setField('excerpt', e.target.value)}
            rows={6}
            placeholder="Leave blank to keep each item's excerpt"
            style={{
              width: '100%', fontFamily: SERIF_STACK, fontWeight: 400,
              fontSize: EDIT_FONT_SIZE, lineHeight: 1.7, color: '#333', padding: '12px 14px',
            }}
          />
        </Box>

        {/* Tags */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mt: '24px', alignItems: 'center' }}>
          {draft.tags.map((tag) => (
            <TagChip key={tag.id || tag.name}>
              {tag.name}
              <ChipRemove type="button" onClick={() => removeTag(tag.name)}>×</ChipRemove>
            </TagChip>
          ))}
          <Autocomplete
            freeSolo
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            size="small"
            options={tagOptions}
            getOptionLabel={optionLabel}
            filterOptions={withCreateOption}
            value={null}
            inputValue={newTag}
            onInputChange={(e, v) => setNewTag(v)}
            onChange={(e, v) => addTag(v)}
            renderOption={renderTagOption}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" placeholder="add tag" sx={autocompleteSx} />
            )}
            slotProps={{
              ...tagPopperSlotProps,
              popper: { ...tagPopperSlotProps.popper, sx: { zIndex: 1600 } },
            }}
            sx={{ width: 200 }}
          />
        </Box>

        {/* Your tracking */}
        <Box sx={{ mt: '26px', pt: '18px', borderTop: '1px solid #eee' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
            <FlagToggle states={favMap} value={draft.fav} title="Favorite" onClick={() => cycleFlag('fav')} />
            <FlagToggle states={hiddenMap} value={draft.hidden} title="Hidden" onClick={() => cycleFlag('hidden')} />
            <Box sx={{ width: '1px', alignSelf: 'stretch', minHeight: 24, background: '#eaeaea', mx: '6px' }} />
            <Box component="span" sx={{ fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY }}>Reviewed</Box>
            <TextInput
              type="datetime-local"
              value={draft.dateReviewed}
              onChange={(e) => setField('dateReviewed', e.target.value)}
              style={{ padding: '6px 10px', fontSize: '0.95rem' }}
            />
            {draft.dateReviewed.trim() && (
              <LinkButton type="button" onClick={() => setField('dateReviewed', '')}>clear</LinkButton>
            )}
          </Box>
        </Box>

        {/* Related */}
        <Box sx={{ mt: '22px', pt: '18px', borderTop: '1px solid #eee' }}>
          <GroupLabel>Set related items</GroupLabel>
          {draft.related.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mb: '10px' }}>
              {draft.related.map((r) => (
                <TagChip key={r.id}>
                  {`#${r.id}`}
                  <ChipRemove type="button" onClick={() => removeRelated(r.id)}>×</ChipRemove>
                </TagChip>
              ))}
            </Box>
          )}
          <TextInput
            value={newRelated}
            onChange={(e) => setNewRelated(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRelated(); } }}
            placeholder="Add related item by ID, press Enter"
            style={{ width: '100%', maxWidth: 320 }}
          />
        </Box>
      </Box>
    </Box>
  );
}

BatchEditHeroPage.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  namespace: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};

BatchEditHeroPage.defaultProps = {
  namespace: 'private',
  onSaved: null,
};
